import React, { useState } from 'react';
import { useApp } from '../../AppContext';

interface Props {
  uid: string;
  token: string;
  onDone: () => void;
}

export default function ResetPasswordPage({ uid, token, onDone }: Props) {
  const { confirmPasswordReset } = useApp();
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (pwd.length < 8) { setError('La password deve essere di almeno 8 caratteri.'); return; }
    if (pwd !== confirm) { setError('Le password non coincidono.'); return; }
    setLoading(true);
    try {
      await confirmPasswordReset(uid, token, pwd, confirm);
      setSuccess(true);
    } catch (e: any) {
      const data = e?.data;
      let msg = e?.message || 'Impossibile reimpostare la password.';
      if (data?.new_password && Array.isArray(data.new_password)) msg = data.new_password.join(' ');
      else if (data?.detail) msg = data.detail;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-forestDark flex items-center justify-center px-6 overflow-hidden relative">
      <div className="absolute inset-0 pointer-events-none grain-overlay opacity-60" />
      <div className="gate-blob gate-blob-1" />
      <div className="gate-blob gate-blob-2" />

      <div className="gate-card-anim relative z-10 w-full max-w-md bg-cream rounded-3xl border border-forest/10 shadow-2xl p-8 md:p-10 space-y-6">
        <div className="space-y-1 text-center">
          <span className="font-serif text-2xl font-semibold tracking-tight text-forestDark block">Dr. Michele Balducci</span>
          <span className="text-xs tracking-[0.2em] uppercase text-sage font-medium">Reset Password</span>
        </div>

        {success ? (
          <div className="space-y-4 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h3 className="font-serif text-xl text-forestDark">Password reimpostata!</h3>
            <p className="text-xs text-charcoal/70">Ora puoi accedere con la nuova password.</p>
            <button
              onClick={onDone}
              className="btn-shine w-full bg-forest hover:bg-forestDark text-cream py-3 rounded-full text-sm font-semibold smooth-transition hover:-translate-y-0.5"
            >
              Vai al sito
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-2 text-center">
              <h2 className="font-serif text-2xl text-forestDark">Imposta una nuova password</h2>
              <p className="text-xs text-charcoal/60">Scegli una password forte di almeno 8 caratteri.</p>
            </div>

            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-charcoal/70">Nuova password</label>
                <input
                  type="password"
                  value={pwd}
                  onChange={e => setPwd(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submit(); }}
                  autoFocus
                  autoComplete="new-password"
                  className="w-full bg-beigeBG border border-forest/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/15 smooth-transition"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-charcoal/70">Conferma nuova password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submit(); }}
                  autoComplete="new-password"
                  className="w-full bg-beigeBG border border-forest/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/15 smooth-transition"
                />
                {error && <p className="text-xs text-rose-700 font-medium pt-1">{error}</p>}
              </div>
            </div>

            <button
              onClick={submit}
              disabled={loading}
              className="btn-shine w-full bg-forest hover:bg-forestDark text-cream py-3.5 rounded-full text-sm font-semibold smooth-transition hover:-translate-y-0.5 disabled:opacity-60"
            >
              {loading ? 'Salvataggio…' : 'Reimposta password'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
